import React, { useEffect, useMemo, useState } from "react";
import ExamEditWidget from "./components/ExamEditWidget";
import EditDuoxuan from "./components/EditDuoxuan";
import EditZhuguan from "./components/EditZhuguan";
import HomeworkLayout from "../../homework/component/layout";
import {
    getEmptyTopicChoice,
    getEmptyTopicCompound,
    TopicType
} from "../../../service/homework";
import useRequest from "../../../utils/useRequest";
import PaperContext from "./context";
import LoadingWrapper from "../../../components/LoadingWrapper";
import EditDanXuan from "./components/EditDanXuan";
import {
    errorMessage,
    getTypeSort,
    isTrue,
    QuestionType,
    SortType
} from "../../../service/service";
import Header from "../../../components/Header";
import { Empty, message, Spin } from "antd";
import style from "./addExam.module.scss";
import EditPanDuan from "./components/EditPanDuan";
import {
    ExamListItem,
    ExamPaperListItem,
    getExamInfo,
    getExamPaper,
    getExamPaperList,
    paperTopicSort
} from "../../../service/exam";
import _ from "underscore";
import EditTianKong from "./components/EditTiankong";
import EditCompound from "./components/EditCompound";
import FieldTypes from "./components/FieldTypes";
import useRequestFn from "utils/useRequestFn";
import ResultMessage from "components/CourseComponents/ResultMessage";

interface PageAddExamProps {
    match: {
        params: {
            examId: string;
            paperId: string;
            readonly: string;
        };
    };
}

export interface OrderTopicType extends TopicType {
    order?: number;
}

export const AllTopicType = [
    QuestionType.DanXuan,
    QuestionType.DuoXuan,
    QuestionType.PanDuan,
    QuestionType.ZhuGuan,
    QuestionType.TianKong,
    QuestionType.TingLi,
    QuestionType.WanXingTianKong,
    QuestionType.YueDuLiJie
];

export default function PageAddExam({ match: { params } }: PageAddExamProps) {
    const [paperId, setPaperId] = useState(""); //试卷id
    const [examPaperList, setExamPaperList] = useState<ExamPaperListItem[]>([]); //试卷列表
    const [checked, setChecked] = useState(0); //选中
    const [getData, { loading, data: paperData }] = useRequestFn(getExamPaper);
    const [examData, { loading: loadingExamData }] = useRequest(() =>
        getExamInfo(params.examId)
    );

    const [topics, setTopics] = useState<OrderTopicType[]>([]);
    const [topicTypes, setTopicTypes] = useState<QuestionType[]>([]); //试题类型
    const [newTopic, setNewTopic] = useState("");
    const [getExamTypeList, { loading: sortLoading }] =
        useRequestFn(getTypeSort);

    useEffect(() => {
        if (examPaperList?.length !== 0) {
            setPaperId(examPaperList[checked].paperId);
        } else {
            setPaperId("");
            setTopics([]);
        }
    }, [examPaperList]);

    useEffect(() => {
        if (!paperId) return;
        loadPaperData();
    }, [paperId]);

    async function loadPaperData() {
        try {
            await getData(paperId);
        } catch (e) {
            ResultMessage.error(errorMessage(e));
        }
    }

    useEffect(() => {
        if (!paperData) {
            return setTopicTypes(AllTopicType);
        }
        if (paperData.topics.length !== 0) {
            const topics: OrderTopicType[] = [...paperData.topics];
            const keys = _.uniq(_.map(topics, topic => topic.topicType));
            const topicGroup = _.groupBy(
                paperData.topics,
                topic => topic.topicType
            );
            topics.map(topic => {
                const list = topicGroup[topic.topicType];
                topic.order = list.findIndex(item => item === topic);
                return topic;
            });
            setTopics(topics);
        } else {
            setTopics([]);
        }

        getExamTypeList(SortType.Exam, parseInt(paperData?.paperId) || 0).then(
            res => {
                setTopicTypes(res.split(",") as QuestionType[]);
            }
        );
    }, [paperData]);

    const handleSortType = (topicTypes: QuestionType[], sort: boolean) => {
        setTopicTypes(topicTypes);
        if (!paperId) return;
        sort && paperTopicSort(paperData!.paperId, topicTypes);
    };

    // 当前试卷是否有学员在做(有的话不允许修改试卷和试题)
    const lockEdit = useMemo(() => {
        if (!examData) return false;
        if (params.readonly === "1") return true;
        if (isTrue(examData.studentDo)) return true;
        return examData ? !isTrue(examData.editable) : false;
    }, [examData, params.readonly]);

    // 试卷加载时的提示
    useEffect(() => {
        if (examData && !isTrue(examData.editable)) {
            message.warn("您不是出卷老师, 无法进行操作!");
            return;
        }
        lockEdit &&
            params.readonly === "0" &&
            message.warn("学生已经开始答题, 锁定更改操作!");
    }, [lockEdit]);

    // 添加试题
    const addTopic = (type: QuestionType) => {
        if (lockEdit) return; // 如果有学员答题则不允许修改

        let topic: OrderTopicType;
        if (
            type ===
            (QuestionType.TingLi ||
                QuestionType.WanXingTianKong ||
                QuestionType.YueDuLiJie)
        ) {
            topic = getEmptyTopicCompound(type);
        } else {
            topic = getEmptyTopicChoice(type);
        }

        setTopics([...topics, topic]);
        setNewTopic(`${topic.topicId || topic.tempTopicId}`);
    };

    useEffect(() => {
        if (!newTopic) return;
        const interval = setInterval(() => {
            let anchorElement = document.getElementById(newTopic);
            if (anchorElement) {
                anchorElement.scrollIntoView();
                clearInterval(interval);
            }
        }, 100);
    }, [newTopic]);

    // 试题内容更改
    const onTopicChange = (index: number, topic: OrderTopicType) => {
        const newTopics = [...topics];
        const topicIndex = newTopics.findIndex(
            item => item.tempTopicId === topic.tempTopicId
        );
        topic.order = newTopics[topicIndex].order;
        newTopics[topicIndex] = { ...newTopics[topicIndex], ...topic };
        setTopics(newTopics);
    };

    return (
        <PageAddExamPure
            loading={loading}
            loadingExamData={loadingExamData}
            paperId={paperId}
            setPaperId={setPaperId}
            lockEdit={lockEdit}
            handleSortType={handleSortType}
            topicTypes={topicTypes}
            topics={topics}
            onTopicChange={onTopicChange}
            setTopics={setTopics}
            addTopic={addTopic}
            examData={examData}
            loadPaperData={loadPaperData}
            examId={params.examId}
            setExamPaperList={setExamPaperList}
            setChecked={setChecked}
            checked={checked}
        />
    );
}

export interface PageAddExamPureProps {
    loading: boolean;
    loadingExamData: boolean;
    paperId: string;
    setPaperId: (val: string) => void;
    lockEdit: boolean;
    handleSortType: (topicTypes: QuestionType[], sort: boolean) => any;
    topicTypes: QuestionType[];
    topics: OrderTopicType[];
    onTopicChange: (index: number, topic: OrderTopicType) => any;
    setTopics: (val: OrderTopicType[]) => void;
    addTopic: (type: QuestionType) => void;
    examData: ExamListItem | null;
    loadPaperData: () => Promise<void>;
    examId: string;
    setExamPaperList: (val: ExamPaperListItem[]) => void;
    setChecked: (val: number) => void;
    checked: number;
}

export function PageAddExamPure({
    loading,
    handleSortType,
    loadingExamData,
    lockEdit,
    onTopicChange,
    paperId,
    setPaperId,
    topics,
    topicTypes,
    addTopic,
    examData,
    loadPaperData,
    setTopics,
    examId,
    setExamPaperList,
    setChecked,
    checked
}: PageAddExamPureProps) {
    const [examPaperData, { loading: loadingPaperList }, loadExamPaperData] =
        useRequest(() => getExamPaperList(examId), {}, [examId]);

    const onChange = (val: string) => {
        setPaperId(val);
        const index = examPaperData?.findIndex(item => item.paperId === val);
        if (!index) {
            setChecked(0);
        } else {
            setChecked(index);
        }
    };

    useEffect(() => {
        if (!examPaperData) return;
        setExamPaperList(examPaperData);
    }, [examPaperData]);

    return (
        <div>
            <Header />

            <LoadingWrapper loading={false} isEmpty={false}>
                {() => (
                    <PaperContext.Provider
                        value={{
                            paperId: paperId,
                            setPaperId,
                            lockEdit: lockEdit,
                            topics,
                            setTopics,
                            addTopic,
                            examData,
                            loadPaperData,
                            loadExamPaperData,
                            examPaperData: examPaperData || [],
                            examId,
                            checked,
                            setChecked
                        }}>
                        <HomeworkLayout
                            left={
                                <ExamEditWidget
                                    sortType={handleSortType}
                                    examTypeList={topicTypes}
                                />
                            }
                            main={
                                <div>
                                    <div className={style.tip}>
                                        温馨提示：新增的试卷将会在本次考试中随机分配给考生，请勿添加非本门课程的试卷，或非本次考试的试卷。
                                    </div>
                                    {examPaperData &&
                                        examPaperData.length !== 0 && (
                                            <FieldTypes
                                                value={paperId}
                                                onChange={onChange}
                                                examPaperData={examPaperData}
                                                loading={loadingPaperList}
                                            />
                                        )}

                                    <Spin spinning={loading || loadingExamData}>
                                        {topicTypes.map(type => {
                                            return _.chain(topics)
                                                .filter(
                                                    topic =>
                                                        topic.topicType === type
                                                )
                                                .sortBy(topic => topic.order)
                                                .map((item, index) => (
                                                    <div
                                                        key={`${
                                                            item.topicId ||
                                                            item.tempTopicId
                                                        }`}
                                                        id={`${
                                                            item.topicId ||
                                                            item.tempTopicId
                                                        }`}>
                                                        {item.topicType ===
                                                            QuestionType.DanXuan && (
                                                            <EditDanXuan
                                                                topic={item}
                                                                onChange={topic =>
                                                                    onTopicChange(
                                                                        index,
                                                                        topic
                                                                    )
                                                                }
                                                            />
                                                        )}
                                                        {item.topicType ===
                                                            QuestionType.DuoXuan && (
                                                            <EditDuoxuan
                                                                topic={item}
                                                                onChange={topic =>
                                                                    onTopicChange(
                                                                        index,
                                                                        topic
                                                                    )
                                                                }
                                                            />
                                                        )}
                                                        {item.topicType ===
                                                            QuestionType.PanDuan && (
                                                            <EditPanDuan
                                                                topic={item}
                                                                onChange={topic =>
                                                                    onTopicChange(
                                                                        index,
                                                                        topic
                                                                    )
                                                                }
                                                            />
                                                        )}
                                                        {item.topicType ===
                                                            QuestionType.ZhuGuan && (
                                                            <EditZhuguan
                                                                topic={item}
                                                                onChange={topic =>
                                                                    onTopicChange(
                                                                        index,
                                                                        topic
                                                                    )
                                                                }
                                                            />
                                                        )}
                                                        {item.topicType ===
                                                            QuestionType.TianKong && (
                                                            <EditTianKong
                                                                topic={item}
                                                                onChange={topic =>
                                                                    onTopicChange(
                                                                        index,
                                                                        topic
                                                                    )
                                                                }
                                                            />
                                                        )}
                                                        {item.topicType ===
                                                            QuestionType.TingLi && (
                                                            <EditCompound
                                                                topic={item}
                                                                onChange={topic =>
                                                                    onTopicChange(
                                                                        index,
                                                                        topic
                                                                    )
                                                                }
                                                            />
                                                        )}
                                                        {item.topicType ===
                                                            QuestionType.WanXingTianKong && (
                                                            <EditCompound
                                                                topic={item}
                                                                onChange={topic =>
                                                                    onTopicChange(
                                                                        index,
                                                                        topic
                                                                    )
                                                                }
                                                            />
                                                        )}
                                                        {item.topicType ===
                                                            QuestionType.YueDuLiJie && (
                                                            <EditCompound
                                                                topic={item}
                                                                onChange={topic =>
                                                                    onTopicChange(
                                                                        index,
                                                                        topic
                                                                    )
                                                                }
                                                            />
                                                        )}
                                                    </div>
                                                ))
                                                .value();
                                        })}
                                        {topics.length === 0 && (
                                            <div className={style.Empty}>
                                                <Empty description="请先点击左侧的“增加题型”按钮" />
                                            </div>
                                        )}
                                    </Spin>
                                </div>
                            }
                        />
                    </PaperContext.Provider>
                )}
            </LoadingWrapper>
        </div>
    );
}
